import { useEffect, useState } from 'react';

/**
 * Sidebar "Social Platform" filter display style — some admins prefer the
 * icon-button grid (visual, fast to scan by logo), others prefer a checkbox
 * dropdown (compact, easier to find a specific platform by name). A /settings
 * preference instead of forcing one on everyone.
 *
 * Storage/event pattern mirrors tutorialMode.ts. Unlike Tutorial Mode this
 * doesn't need a Context provider — it's read by a handful of sidebar
 * components (ChannelFilterSidebar, rosters/index, campaigns/index), not
 * hundreds of per-row instances, so a plain hook subscribing directly to the
 * change event is enough.
 */
export type PlatformFilterStyle = 'buttons' | 'dropdown';

const STORAGE_KEY = 'ggc_platform_filter_style';

export const PLATFORM_FILTER_STYLE_EVENT = 'ggc:platform-filter-style-change';

export const DEFAULT_PLATFORM_FILTER_STYLE: PlatformFilterStyle = 'buttons';

export function getPlatformFilterStyle(): PlatformFilterStyle {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'buttons' || stored === 'dropdown') return stored;
  } catch { /* ignore */ }
  return DEFAULT_PLATFORM_FILTER_STYLE;
}

export function setPlatformFilterStyle(style: PlatformFilterStyle): void {
  try {
    localStorage.setItem(STORAGE_KEY, style);
  } catch { /* ignore */ }
  window.dispatchEvent(
    new CustomEvent<PlatformFilterStyle>(PLATFORM_FILTER_STYLE_EVENT, { detail: style }),
  );
}

/** Reactive read — updates live if the setting changes on /settings while this
 *  component is mounted (e.g. two tabs open), without a per-instance localStorage poll. */
export function usePlatformFilterStyle(): PlatformFilterStyle {
  const [style, setStyle] = useState<PlatformFilterStyle>(getPlatformFilterStyle);

  useEffect(() => {
    const handler = (e: Event) => {
      setStyle((e as CustomEvent<PlatformFilterStyle>).detail);
    };
    window.addEventListener(PLATFORM_FILTER_STYLE_EVENT, handler);
    return () => window.removeEventListener(PLATFORM_FILTER_STYLE_EVENT, handler);
  }, []);

  return style;
}
